/**
 * Where everything in Northline lives — the one registry of destinations.
 *
 * PORT-13 grew this from the property-summary map it started as (PORT-3) into
 * the whole application's route table, because the consolidation left eleven
 * links pointing at screens that no longer exist. Every one of them had built
 * its own path inline, so PORT-6 could move Construction out of the property
 * record without a single one of them noticing.
 *
 * Two rules, and the first is the reason the second matters.
 *
 * ONE RECORD, ONE ADDRESS. A record is addressed by its own id — a draw by
 * `drawId`, not by `propertyId + projectId + a tab name`. A link that
 * reassembles a path out of the property is stating an ownership the
 * architecture no longer has, and it breaks the moment that workflow moves.
 * Property is context; it does not determine the destination.
 *
 * LISTS ARRIVE FILTERED. A deep link that dumps the operator into an
 * unfiltered portfolio-wide list is slower than the duplicated tab it
 * replaced, and that is how a consolidation like this gets reverted.
 *
 * Both belong in one file for the same reason the modules were consolidated:
 * a route rename then breaks one file instead of leaking a dead link into
 * twelve components and two Python modules, which is exactly what happened.
 *
 * Every list destination below already accepts `property_id` server-side:
 *   work orders   GET /work-orders?property_id=      (PORT-2)
 *   inspections   GET /inspections?property_id=
 *   construction  GET /projects?property_id=         (PORT-3)
 *   leasing       GET /tenants/all?property_id=
 *   accounting    GET /general-ledger?property_id=
 */

const withProperty = (path, propertyId, extra = {}) => {
  const params = new URLSearchParams();
  if (propertyId !== undefined && propertyId !== null && propertyId !== '') {
    params.set('property_id', String(propertyId));
  }
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/* ═══════════════════════════════════════════════════════════════════
 * ONE RECORD, ONE ADDRESS.
 *
 * The canonical destination for a single record, keyed on that record's own
 * id. An alert, a payable, an activity row and a Command Center queue item
 * all reach the same screen through the same function.
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Command Center → My Approvals (APR-11): THE one place a person approves,
 * authorizes or pays. `kind` narrows the list ('payments', 'draws', …);
 * `request` selects one approval request; `payment` / `batch` select a
 * payment or a payment batch under Payments. Every notification, email,
 * alert and module link that means "go approve this" builds its address here.
 */
export const myApprovals = ({ kind, request, payment, batch } = {}) => {
  const params = new URLSearchParams({ tab: 'approvals' });
  if (kind) params.set('kind', String(kind));
  if (request) params.set('request', String(request));
  if (payment) params.set('payment', String(payment));
  if (batch) params.set('batch', String(batch));
  return `/workflow?${params.toString()}`;
};

/** Operations → one work order (PORT-5). */
export const workOrderDetail = (workOrderId) => `/work-orders/${workOrderId}`;

/** Operations → one inspection (PORT-13). */
export const inspectionDetail = (inspectionId) => `/inspections/${inspectionId}`;

/** Construction → one project (PORT-6). */
export const projectDetail = (projectId) => `/construction/projects/${projectId}`;

/**
 * Construction → one draw request (PORT-13).
 *
 * A draw is rendered inside its project's workspace, but it is addressed as
 * itself: the route resolves the draw's own project. That is the whole point
 * — an approval alert carries a `draw_id` and nothing else, so a destination
 * needing `property_id + project_id` forced the alert to rebuild a property
 * path, and that path is what died with the Projects tab.
 */
export const drawDetail = (drawId) => `/construction/draws/${drawId}`;

/** Accounting → the general ledger, filtered to one account. */
export const ledgerForAccount = (accountId) => `/accounts?tab=ledger&account_id=${accountId}`;

/** Accounting → one payable, inside the payables workflow (PORT-13). */
export const payableDetail = (payableId) => `/accounts?tab=invoices&bill=${payableId}`;

/** Operations → one tenant. */
export const tenantDetail = (tenantId) => `/tenants/${tenantId}`;

/** Portfolio → the property record itself. Context, never a workflow. */
export const propertyDetail = (propertyId) => `/properties/${propertyId}`;


/* ═══════════════════════════════════════════════════════════════════
 * LISTS ARRIVE FILTERED — a module's own list, narrowed to one property.
 * ═══════════════════════════════════════════════════════════════════ */

/** Operations → Work Orders, filtered to this property. */
export const workOrdersFor = (propertyId, { status } = {}) =>
  withProperty('/work-orders', propertyId, { status });

/** Operations → Inspections, filtered to this property. */
export const inspectionsFor = (propertyId, { status } = {}) =>
  withProperty('/inspections', propertyId, { status });

/** Operations → Construction, filtered to this property. */
export const constructionFor = (propertyId) =>
  withProperty('/construction', propertyId);

/** Operations → Tenants & Leasing, filtered to this property. */
export const leasingFor = (propertyId, { tab } = {}) =>
  withProperty('/tenants', propertyId, { tab });

/**
 * Leasing → the new-tenant form, already pointed at one unit (PORT-12).
 *
 * Portfolio's Units tab used to create the tenant and the lease itself. It
 * hands off now, and a hand-off that lands on an unfiltered tenant list is
 * worse than the dialog it replaced — that is how a consolidation gets
 * reverted. This arrives with the form open on the unit the operator clicked.
 */
export const newTenantFor = (propertyId, { unitId } = {}) =>
  withProperty('/tenants', propertyId, { new_tenant: '1', unit_id: unitId });

/** Finance → Accounting, general ledger filtered to this property. */
export const accountingFor = (propertyId, { tab = 'ledger' } = {}) =>
  withProperty('/accounts', propertyId, { tab });

/** Finance → Capital Events, filtered to this property. */
export const capitalEventsFor = (propertyId) =>
  withProperty('/capital-events', propertyId);

/**
 * Reports → the Profit & Loss for this property (PORT-11).
 *
 * `report` and `preset` are read by ReportsPage on mount, so this arrives on
 * the right report over the right period rather than on the default one — a
 * link that lands on a portfolio-wide YTD P&L is worse than no link.
 */
export const profitAndLossFor = (propertyId, { preset = 'ytd' } = {}) =>
  withProperty('/reports', propertyId, { report: 'profit_and_loss', preset });

/** The property's own Documents tab — the one of the five that owns its data. */
export const propertyDocumentsFor = (propertyId) =>
  `/properties/${propertyId}?tab=documents`;

/**
 * Where one Activity event opens (PORT-10).
 *
 * The event stream is the property's window onto modules it no longer owns,
 * so every row has to be able to hand the operator to the module that does.
 * The backend deliberately does not know these paths: it returns `kind` plus
 * `entity_id` (and `ref_id` for the record the module actually opens — a
 * draw opens inside its project, a lease inside its tenant), and the map
 * from that to a route stays here with the rest of them.
 *
 * Returns `null` for a kind that has no destination — a property-record edit
 * is already on screen — and callers render those rows without a link rather
 * than sending anyone somewhere useless.
 */
export const eventDestination = (event, propertyId) => {
  if (!event) return null;
  const { kind, entity_id: entityId, ref_id: refId } = event;
  switch (kind) {
    case 'work_order':
      return entityId ? { path: workOrderDetail(entityId), label: 'Open work order' } : null;
    case 'inspection':
      // PORT-13 — an inspection is addressable now, so the row opens the
      // inspection rather than a list filtered to its property.
      return entityId
        ? { path: inspectionDetail(entityId), label: 'Open inspection' }
        : { path: inspectionsFor(propertyId), label: 'Open in Inspections' };
    case 'project':
      return entityId ? { path: projectDetail(entityId), label: 'Open project' } : null;
    case 'draw':
      // PORT-13 — the draw itself, by its own id. `ref_id` (the project) is
      // the fallback for a stream row written before draws were addressable.
      if (entityId) return { path: drawDetail(entityId), label: 'Open draw' };
      return refId ? { path: projectDetail(refId), label: 'Open project' } : null;
    case 'lease':
      return refId
        ? { path: tenantDetail(refId), label: 'Open tenant' }
        : { path: leasingFor(propertyId), label: 'Open in Leasing' };
    case 'transaction':
      return { path: accountingFor(propertyId), label: 'Open in Accounting' };
    case 'document':
      return { path: propertyDocumentsFor(propertyId), label: 'Open Documents' };
    case 'capital_event':
      return { path: capitalEventsFor(propertyId), label: 'Open in Capital Events' };
    default:
      return null;
  }
};

/**
 * Where a journal entry's source opens (RPT-E).
 *
 * A report drill-down ends at the record that created the entry: the bill,
 * the work order, the draw, the tenant whose receipt it was. The backend
 * resolves the entry to `{kind, entity_id, ref_id, label}` and, as with
 * `eventDestination`, does not know these paths. A kind with no destination
 * renders as its label — never a link to nowhere.
 */
export const sourceDestination = (source) => {
  if (!source) return null;
  const { kind, entity_id: entityId, ref_id: refId } = source;
  switch (kind) {
    case 'bill':
      return entityId ? { path: payableDetail(entityId), label: 'Open bill' } : null;
    case 'work_order':
      return entityId ? { path: workOrderDetail(entityId), label: 'Open work order' } : null;
    case 'draw':
      return entityId ? { path: drawDetail(entityId), label: 'Open draw' } : null;
    case 'tenant':
      return entityId ? { path: tenantDetail(entityId), label: 'Open tenant' } : null;
    case 'capital_event':
      return refId ? { path: capitalEventsFor(refId), label: 'Open capital events' } : null;
    default:
      // `journal_entry` is opened in place by the ledger dialog, not routed.
      return null;
  }
};

export default {
  workOrderDetail,
  inspectionDetail,
  projectDetail,
  drawDetail,
  payableDetail,
  tenantDetail,
  propertyDetail,
  workOrdersFor,
  inspectionsFor,
  constructionFor,
  leasingFor,
  newTenantFor,
  accountingFor,
  capitalEventsFor,
  profitAndLossFor,
  propertyDocumentsFor,
  eventDestination,
  sourceDestination,
};
